import { useEffect, useRef, useState } from "react"
import type { ProjectMainTab, ProjectRailTab, ProjectRecord } from "./types"

export function formatDate(iso: string | undefined) {
  if (!iso) return "—"
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return "—"
  return date.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

export function toInputDateValue(date: Date | undefined) {
  if (!date) return ""
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

export function parseInputDateValue(value: string) {
  if (!value) return undefined
  const date = new Date(`${value}T00:00:00`)
  return Number.isNaN(date.getTime()) ? undefined : date
}

export function addDays(date: Date, days: number) {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

export function defaultMilestoneDateValue() {
  return toInputDateValue(addDays(new Date(), 7))
}

export function milestoneDateSortValue(iso: string | undefined) {
  if (!iso) return Number.MAX_SAFE_INTEGER
  const time = new Date(iso).getTime()
  return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time
}

export function milestoneBadgeLabel(isDone: boolean) {
  return isDone ? "Достигнуто" : "Не достигнуто"
}

export function isProjectMainTab(value: string | null): value is ProjectMainTab {
  return (
    value === "overview" ||
    value === "tasks" ||
    value === "timeline" ||
    value === "dashboard" ||
    value === "files" ||
    value === "activity"
  )
}

export function isProjectRailTab(value: string | null): value is ProjectRailTab {
  return value === "fields" || value === "status" || value === "activity"
}

export function useProjectHighlights(project: ProjectRecord | null): Set<string> {
  const [highlighted, setHighlighted] = useState<Set<string>>(new Set())
  const prevRef = useRef<ProjectRecord | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const applyRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const prev = prevRef.current
    prevRef.current = project

    if (!project || !prev || project.id !== prev.id) return

    const fields = [
      "name",
      "description",
      "state",
      "ownerId",
      "startDate",
      "endDate",
    ] as const

    const changed = new Set<string>()
    for (const field of fields) {
      if (prev[field] !== project[field]) changed.add(field)
    }
    if (prev.status?.slug !== project.status?.slug) changed.add("status")
    if (changed.size === 0) return

    if (applyRef.current) clearTimeout(applyRef.current)
    applyRef.current = setTimeout(() => {
      setHighlighted(changed)
    }, 0)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setHighlighted(new Set()), 2500)

    return () => {
      if (applyRef.current) clearTimeout(applyRef.current)
    }
  }, [project])

  return highlighted
}
